import { ReactElement, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from 'components/UI/MDC/Card/Card';
import { Headline4 } from 'components/UI/MDC/Typography/Typography';
import { PAGE_ICONS, PAGE_ROUTES } from 'pages/AnnualUserAccessReview/_const';
import PageWithBreadcrumbs from 'pages/AnnualUserAccessReview/layouts/UARPageWithBreadcrumbs';
import { getLegacyPath } from 'routes/_utils';
import { selectProcessedRequests } from 'store/uar/selectors';

const getRequestDetailsUrl = trackingNumber => {
	return getLegacyPath(`/RRS/RequestDetails.aspx?tn=${trackingNumber}&ref=wqr`);
};

const getActionChipColorHandler = action => {
	const actionChipColors = {
		MODIFY: 'warning',
		REVOKE: 'error',
	};

	return actionChipColors[action] || 'info';
};

const pageShellSx = {
	width: '100%',
	display: 'flex',
	justifyContent: 'center',
	px: { xs: 2, sm: 3, md: 4 },
	py: { xs: 2, sm: 3 },
};

const pageContentSx = {
	width: '100%',
	maxWidth: 1080,
	margin: '0 auto',
};

/**
 * View for displaying processed tracking numbers and action summary after review submission.
 * @returns {ReactElement} processed requests page view
 */
const ProcessedRequestsView = ({
	embedded,
	onBackToReview,
	onBackToHome,
}) => {
	const history = useHistory();
	const processedRequests = useSelector(selectProcessedRequests);
	const sProcessedEntries = useMemo(() => {
		return processedRequests
			.map(entry => {
				return {
					trackingNumber: entry?.trackingNumber || null,
					staffMemberName: entry?.staffMemberName || 'Unknown User',
					staffMemberId: entry?.staffMemberId || null,
					appActions: Array.isArray(entry?.appActions)
						? entry.appActions
						: [],
				};
			})
			.filter(entry => entry.trackingNumber);
	}, [processedRequests]);

	const onBackToReviewHandler = () => {
		const onBackHandler = onBackToReview || (() => history.push(PAGE_ROUTES.REVIEW));
		onBackHandler();
	};

	const onBackToHomeHandler = () => {
		const onBackHandler = onBackToHome || (() => history.push(PAGE_ROUTES.HOME));
		onBackHandler();
	};

	const content = (
		<>
			<Headline4>Requests Processed</Headline4>
			<Alert severity='success' sx={{ mb: 2 }}>
				Your User Access Review actions have been successfully submitted.
			</Alert>

			{sProcessedEntries.length === 0 ? (
				<Alert severity='info' sx={{ mb: 2 }}>
					No processed request details were found for this session.
				</Alert>
			) : (
				<List>
					{sProcessedEntries.map((entry, index) => {
						const actionApps = entry.appActions.map(appAction => {
							return {
								action: String(
									appAction?.action || 'MODIFY'
								).toUpperCase(),
								appName: appAction?.appName || 'Unknown App',
							};
						});

						return (
							<ListItem key={entry.trackingNumber} divider>
								<ListItemText
									primary={
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												gap: 1,
												flexWrap: 'wrap',
											}}
										>
											<Typography component='span' variant='body1'>
												{`${index + 1}. ${entry.staffMemberName}`}
											</Typography>
											{actionApps.map((actionApp, actionIndex) => {
												return (
													<Box
														key={`${entry.trackingNumber}-${actionApp.action}-${actionApp.appName}-${actionIndex}`}
														sx={{
															display: 'inline-flex',
															alignItems: 'center',
															gap: 0.5,
														}}
													>
														<Chip
															size='small'
															label={actionApp.action}
															color={getActionChipColorHandler(actionApp.action)}
															sx={theme => ({
																fontWeight: 700,
																color: theme.palette.common.white,
																'& .MuiChip-label': {
																	color: theme.palette.common.white,
																},
															})}
														/>
														<Typography component='span' variant='caption'>
															{actionApp.appName}
														</Typography>
													</Box>
												);
											})}
										</Box>
									}
									secondary={
										<>
											<Typography variant='body2' component='span'>
												Tracking #:{' '}
											</Typography>
											<Link
												href={getRequestDetailsUrl(entry.trackingNumber)}
												target='_blank'
												rel='noopener noreferrer'
												underline='hover'
											>
												{entry.trackingNumber}
											</Link>
										</>
									}
								/>
							</ListItem>
						);
					})}
				</List>
			)}

			<Stack direction='row' spacing={1} sx={{ mt: 2 }}>
				<Button variant='contained' onClick={onBackToReviewHandler}>
					Back to Review
				</Button>
				<Button variant='text' onClick={onBackToHomeHandler}>
					Back to UAR Home
				</Button>
			</Stack>
		</>
	);

	const renderedView = embedded ? (
		<Box sx={{ height: '100%', overflow: 'hidden', ...pageShellSx }}>
			<Box sx={pageContentSx}>{content}</Box>
		</Box>
	) : (
		<PageWithBreadcrumbs
			currentPageLabel={'Requests Processed'}
			CurrentPageIcon={PAGE_ICONS.REVIEW}
		>
			<Box sx={pageShellSx}>
				<Card xlarge style={pageContentSx}>
					{content}
				</Card>
			</Box>
		</PageWithBreadcrumbs>
	);

	return renderedView;
};

ProcessedRequestsView.propTypes = {
	embedded: PropTypes.bool,
	onBackToReview: PropTypes.func,
	onBackToHome: PropTypes.func,
};

ProcessedRequestsView.defaultProps = {
	embedded: false,
	onBackToReview: undefined,
	onBackToHome: undefined,
};

export default ProcessedRequestsView;
